import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import { Alert, Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import DialogContentText from '@mui/material/DialogContentText';
import { FormikProvider, useFormik } from 'formik';
import _ from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { fetchSiteGroup as fetchSiteGroupById } from '@sophocles/api/siteGroup';
import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import SiteAutocomplete from '@sophocles/components/SiteAutocomplete';
import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';
import { changeSiteDevice, createSite } from '@sophocles/entities/Site';
import { getSelectedCustomerSelector } from '@sophocles/features/auth/authSelectors';
import { addToast } from '@sophocles/features/toast';
import { parseApiError } from '@sophocles/utils/apiErrors';
import SiteForm from '@sophocles/views/sites/forms/SiteForm';

function AssignOrCreateDialog({
  customerId,
  siteGroupId,
  locationId,
  radioId,
  // Optional human label for the radio being assigned (typically the
  // ICCID). When provided we surface it as an alert at the top of the
  // dialog so the user can confirm which unit they're onboarding.
  radioLabel,
  open,
  onSubmit,
  onClose,
  ...props
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isCreateMode, setIsCreateMode] = useState(false);
  const [selectedSiteGroup, setSelectedSiteGroup] = useState(null);
  const [timeZone, setTimeZone] = useState(moment.tz.guess());

  const selectedCustomer = useSelector(getSelectedCustomerSelector);

  // Fetch site group object when dialog opens with a pre-selected siteGroupId
  useEffect(() => {
    if (open && siteGroupId && !selectedSiteGroup) {
      fetchSiteGroupById(siteGroupId).then(sg => {
        setSelectedSiteGroup(sg);
      });
    }
  }, [open, siteGroupId]);

  const siteGroupData = selectedSiteGroup?.toJS?.() ?? selectedSiteGroup;
  const requireBilledSiteGroup = siteGroupData?.options?.require_sites_bill_alternate ?? false;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      customerId: customerId ?? selectedCustomer.get('id', null),
      siteGroupId: siteGroupId,
      siteId: null,
      deviceType: 'relay_controller',
      // Create mode fields
      name: '',
      description: '',
      location_description: '',
      latitude: '',
      longitude: '',
      time_zone: moment.tz.guess(),
      tag_number: '',
      utility_meter_number: '',
      utility_company: '',
      utility_account_number: '',
      billed_site_group: null,
    },
    validationSchema: isCreateMode
      ? yup.object({
          customerId: yup.number().required(),
          siteGroupId: yup.number().required(),
          name: yup.string().required(),
          description: yup.string(),
          location_description: yup.string(),
          latitude: yup.number().required(),
          longitude: yup.number().required(),
          tag_number: yup.string(),
          utility_meter_number: yup.string(),
          utility_company: yup.string(),
          utility_account_number: yup.string(),
          billed_site_group: requireBilledSiteGroup ? yup.number().required() : yup.number().nullable(),
        })
      : yup.object({
          customerId: yup.number().nullable(),
          siteGroupId: yup.number().nullable(),
          siteId: yup.number().nullable(),
          deviceType: yup.string().nullable(),
        }),
    onSubmit: values => {
      if (isCreateMode) {
        // eslint-disable-next-line no-use-before-define -- formik <-> submit handlers form a submit-time cycle
        handleCreateSubmit(values);
      } else {
        // eslint-disable-next-line no-use-before-define -- formik <-> submit handlers form a submit-time cycle
        handleAssignSubmit(values);
      }
    },
  });

  // Rejects when the assignment fails so callers cannot navigate away from a
  // site whose radio never attached. The thunk resolves either way, reporting
  // failure as an `error` key on the resolved value.
  const changeDevice = newSiteId => {
    if (!radioId) {
      dispatch(
        addToast({
          title: t('general.error', 'Error'),
          message: t('inventory.missingRadioForAssign', 'No radio to assign.'),
          level: 'error',
          dismissible: true,
        }),
      );
      return Promise.reject(new Error('AssignOrCreateDialog: radioId is required'));
    }

    return dispatch(changeSiteDevice(newSiteId, { radio: radioId, device_type: formik.values.deviceType })).then(
      values => {
        if (_.has(values, 'error')) {
          const errorText = parseApiError(values.error, {
            genericMessage: null,
            knownFields: [],
          }).nonFieldMessages.join(', ');
          dispatch(
            addToast({
              title: t('general.error', 'Error'),
              message: errorText
                ? `${t('sites.failedToAssignMessage')}: ${errorText}`
                : `${t('sites.failedToAssignMessage')}.`,
              level: 'error',
              dismissible: true,
            }),
          );
          throw new Error('changeSiteDevice failed');
        }

        dispatch(
          addToast({
            message: t('inventory.deviceAssignedToStructure'),
            level: 'success',
            dismissible: true,
          }),
        );
      },
    );
  };

  const handleClose = () => {
    formik.resetForm();
    setIsCreateMode(false);
    setSelectedSiteGroup(null);
    setTimeZone(moment.tz.guess());
    onClose();
  };

  const handleAssignSubmit = values => {
    const { siteId } = values;

    changeDevice(siteId)
      .then(() => {
        if (onSubmit) {
          onSubmit({ create: false, siteId, values });
        }
        navigate(`/c/${formik.values.customerId}/plant/${formik.values.siteGroupId}/structure/${siteId}`);
        handleClose();
      })
      .catch(() => {
        // changeDevice has already surfaced the failure; the dialog stays open
        // so the user can retry against the same site.
      });
  };

  const handleCreateSubmit = values => {
    const siteValues = {
      name: values.name,
      description: values.description,
      location_description: values.location_description,
      latitude: values.latitude ? parseFloat(values.latitude).toFixed(8) : null,
      longitude: values.longitude ? parseFloat(values.longitude).toFixed(8) : null,
      time_zone: timeZone,
      primary_site_group: values.siteGroupId,
      billed_site_group: values.billed_site_group,
      configuration: {
        tag_number: values.tag_number,
        utility_meter_number: values.utility_meter_number,
        utility_company: values.utility_company,
        utility_account_number: values.utility_account_number,
      },
    };

    dispatch(createSite(siteValues))
      .then(result => {
        if (_.has(result, 'error')) {
          const errorData = result.error.response.data;
          formik.setErrors(errorData);
        } else {
          const newSite = result.sites.first();
          const newSiteId = newSite.get('id');

          dispatch(
            addToast({
              title: t('general.success'),
              message: t('inventory.siteCreated', { name: newSite.get('name') }),
              level: 'success',
              dismissible: true,
            }),
          );

          // Assign the device to the newly created site
          return changeDevice(newSiteId).then(() => {
            if (onSubmit) {
              onSubmit({ create: true, siteId: newSiteId, values: siteValues });
            }
            handleClose();
            navigate(`/c/${formik.values.customerId}/plant/${formik.values.siteGroupId}/structure/${newSiteId}`);
          });
        }
      })
      .catch(error => {
        // The site may exist while the attach failed — changeDevice has already
        // toasted that case. Keep the dialog open rather than reporting success.
        console.error('Error creating site or attaching radio:', error);
      });
  };

  const handleModeChange = () => {
    const newMode = !isCreateMode;
    setIsCreateMode(newMode);

    // Reset fields when switching modes
    if (newMode) {
      // Switching to create mode - reset create fields
      formik.setFieldValue('name', '');
      formik.setFieldValue('description', '');
      formik.setFieldValue('location_description', '');
      formik.setFieldValue('latitude', '');
      formik.setFieldValue('longitude', '');
      formik.setFieldValue('tag_number', '');
      formik.setFieldValue('utility_meter_number', '');
      formik.setFieldValue('utility_company', '');
      formik.setFieldValue('utility_account_number', '');
      formik.setFieldValue('billed_site_group', null);
    } else {
      // Switching to assign mode - reset site selection
      formik.setFieldValue('siteId', null);
    }
  };

  const handleTimeZoneChange = tz => {
    setTimeZone(tz);
  };

  return (
    <ResponsiveDialog
      title={t('inventory.assignCreateSite')}
      open={open}
      canSubmit={
        isCreateMode
          ? formik.values.siteGroupId != null && formik.isValid && formik.dirty
          : formik.values.siteId != null
      }
      onClose={handleClose}
      onSubmit={() => formik.submitForm()}
      {...props}
    >
      {radioLabel && (
        <Alert icon={false} severity="info" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              {t('inventory.onboarding', 'Onboarding')}
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-all' }}>
              {radioLabel}
            </Typography>
          </Stack>
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {t('inventory.placeAt', 'Place this device at')}
        </Typography>
        <ToggleButtonGroup
          value={isCreateMode ? 'create' : 'existing'}
          exclusive
          fullWidth
          size="small"
          onChange={(_e, next) => {
            if (next != null && next !== (isCreateMode ? 'create' : 'existing')) {
              handleModeChange();
            }
          }}
        >
          <ToggleButton value="existing">
            <LocationCityOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            {t('inventory.existingSite', 'Existing site')}
          </ToggleButton>
          <ToggleButton value="create">
            <AddBusinessOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            {t('inventory.newSite', 'New site')}
          </ToggleButton>
        </ToggleButtonGroup>
        <DialogContentText sx={{ mt: 1.5, fontSize: 'body2.fontSize' }}>
          {isCreateMode ? t('inventory.createSiteDetails') : t('inventory.assignSiteDetails')}
        </DialogContentText>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        {!customerId && (
          <CustomerAutocomplete
            required
            fullWidth
            name="customerId"
            value={formik.values.customerId || ''}
            onChange={(_e, newCustomerId) => {
              formik.setFieldValue('customerId', newCustomerId);
              formik.setFieldValue('siteGroupId', null);
              formik.setFieldValue('siteId', null);
            }}
          />
        )}
        <SiteGroupAutocomplete
          required
          fullWidth
          name="siteGroupId"
          disabled={formik.values.customerId == null}
          customerId={formik.values.customerId}
          value={formik.values.siteGroupId}
          onChange={(_e, newSiteGroupId, siteGroup) => {
            formik.setFieldValue('siteGroupId', newSiteGroupId);
            formik.setFieldValue('siteId', null);
            setSelectedSiteGroup(siteGroup);
          }}
        />
      </Stack>
      {!isCreateMode && (
        <Stack spacing={2}>
          <SiteAutocomplete
            fullWidth
            required
            name="siteId"
            disabled={formik.values.siteGroupId == null}
            siteGroupId={formik.values.siteGroupId}
            error={formik.errors.siteId != null}
            value={formik.values.siteId || ''}
            onChange={(_e, opt) => {
              formik.setFieldValue('siteId', opt?.value ?? null);
            }}
            onBlur={formik.handleBlur}
            filters={{
              relay_controller__radio__isnull: true,
            }}
          />
        </Stack>
      )}
      {isCreateMode && selectedSiteGroup && (
        <FormikProvider value={formik}>
          <SiteForm
            siteGroup={selectedSiteGroup}
            customerId={formik.values.customerId}
            showExtraDetails={true}
            showTabs={false}
            values={formik.values}
            errors={formik.errors}
            touched={formik.touched}
            setFieldValue={formik.setFieldValue}
            onTimeZoneChange={handleTimeZoneChange}
          />
        </FormikProvider>
      )}
      {isCreateMode && !selectedSiteGroup && (
        <DialogContentText sx={{ mt: 2, fontStyle: 'italic' }}>
          {t('inventory.selectSiteGroupToCreate')}
        </DialogContentText>
      )}
    </ResponsiveDialog>
  );
}

export default AssignOrCreateDialog;
